/*
  Scheda prodotto delle liste dello shop (snippets/scheda-prodotto.liquid).

  - Scelta del formato: le pillole sono radio `name="id"`; al cambio si
    aggiorna il prezzo, letto da `data-prezzo` (gia' formattato dal negozio).
  - Quantita': i tasti - / + cambiano il campo, minimo 1.
  - Aggiungi al carrello: POST a /cart/add.js senza lasciare la pagina, poi
    lo stesso evento che usa il modulo prodotto di Horizon
    (CartLinesUpdateEvent): contatore dell'header e cassetto del carrello si
    aggiornano da soli, e il cassetto si apre se cosi' dice l'impostazione
    del tema *Apri il carrello dopo l'aggiunta*.
  - Finestra *Dettagli prodotto* (snippets/finestra-dettagli.liquid): <dialog>
    aperto con showModal(). Esc e clic fuori chiudono, il fuoco torna al
    link. Sul desktop si apre sopra la sua scheda, come nel design.
    L'altezza e' quella a voci della fisarmonica chiuse, misurata all'apertura
    e a ogni ridimensionamento: aprendo una voce la finestra non cresce,
    scorre il corpo, e la voce aperta viene portata in vista.
  - Finestra *Anteprima confezione* (snippets/finestra-confezione.liquid):
    stesso comportamento. Ogni link apre il <dialog> del suo aria-controls.
*/

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const DESKTOP = window.matchMedia('(min-width: 990px)');
const MENO_MOVIMENTO = window.matchMedia('(prefers-reduced-motion: reduce)');
const MARGINE_FINESTRA = 20;

class SchedaProdotto extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;
  /** @type {HTMLDialogElement[]} */
  finestre = [];
  /** @type {HTMLDialogElement | null} la finestra aperta, o l'ultima aperta */
  finestra = null;
  /** @type {HTMLElement | null} */
  apertoDa = null;

  connectedCallback() {
    this.form = this.querySelector('[data-scheda-form]');
    this.finestre = /** @type {HTMLDialogElement[]} */ (Array.from(this.querySelectorAll('dialog.finestra')));

    this.form?.addEventListener('change', this.#alCambio);
    this.form?.addEventListener('submit', this.#alInvio);
    this.addEventListener('click', this.#alClic);
    for (const finestra of this.finestre) {
      finestra.addEventListener('click', this.#alClicFinestra);
      finestra.addEventListener('close', this.#allaChiusura);
      // toggle non risale: si ascolta in cattura
      finestra.addEventListener('toggle', this.#allaVoce, true);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.#alCambio);
    this.form?.removeEventListener('submit', this.#alInvio);
    this.removeEventListener('click', this.#alClic);
    for (const finestra of this.finestre) {
      finestra.removeEventListener('click', this.#alClicFinestra);
      finestra.removeEventListener('close', this.#allaChiusura);
      finestra.removeEventListener('toggle', this.#allaVoce, true);
    }
    window.removeEventListener('resize', this.#alRidimensionamento);
  }

  /** @param {Event} event */
  #alCambio = (event) => {
    const campo = event.target;
    if (!(campo instanceof HTMLInputElement) || campo.type !== 'radio' || campo.name !== 'id') return;

    const prezzo = this.querySelector('[data-scheda-prezzo]');
    if (prezzo && campo.dataset.prezzo) prezzo.textContent = campo.dataset.prezzo;

    const aggiungi = this.querySelector('[data-scheda-aggiungi]');
    if (aggiungi instanceof HTMLButtonElement) aggiungi.disabled = campo.disabled;
    this.#scriviEsito('');
  };

  /** @param {MouseEvent} event */
  #alClic = (event) => {
    const bersaglio = event.target instanceof Element ? event.target : null;
    if (!bersaglio) return;

    const tasto = bersaglio.closest('[data-quantita]');
    if (tasto instanceof HTMLElement && this.contains(tasto)) {
      const campo = this.querySelector('input[name="quantity"]');
      if (!(campo instanceof HTMLInputElement)) return;
      const attuale = parseInt(campo.value, 10) || 1;
      const nuovo = Math.max(1, attuale + Number(tasto.dataset.quantita));
      campo.value = String(nuovo);
      return;
    }

    const link = bersaglio.closest('[data-apri-finestra]');
    if (link instanceof HTMLElement) this.#apriFinestra(link);
  };

  /** @param {HTMLElement} link */
  #apriFinestra(link) {
    const finestra = this.finestre.find((f) => f.id === link.getAttribute('aria-controls'));
    if (!finestra || finestra.open) return;

    this.finestra = finestra;
    this.apertoDa = link;
    this.#posiziona();
    finestra.showModal();
    this.#fissaAltezza();
    const corpo = this.#corpo();
    if (corpo) corpo.scrollTop = 0;
    window.addEventListener('resize', this.#alRidimensionamento);
  }

  /** @returns {HTMLElement | null} */
  #corpo() {
    return this.finestra?.querySelector('.finestra__corpo') ?? null;
  }

  /* Altezza della finestra = quella a voci chiuse, anche se qualcuna e' aperta:
     la classe finestra--misura nasconde il contenuto delle voci il tempo della
     misura (tutto nello stesso frame, non si vede). Oltre il max-height del
     CSS non va: li' scorre il corpo anche a voci chiuse. */
  #fissaAltezza() {
    const finestra = this.finestra;
    if (!finestra?.open) return;

    const corpo = this.#corpo();
    const scorrimento = corpo?.scrollTop ?? 0;
    finestra.style.removeProperty('height');
    finestra.classList.add('finestra--misura');
    const altezza = Math.ceil(finestra.getBoundingClientRect().height);
    finestra.classList.remove('finestra--misura');
    finestra.style.height = `${altezza}px`;
    if (corpo) corpo.scrollTop = scorrimento;
  }

  #alRidimensionamento = () => {
    this.#posiziona();
    this.#fissaAltezza();
  };

  /* Voce aperta: se il suo contenuto finisce sotto il bordo del corpo, lo si
     fa scorrere quanto basta, senza mai portare il titolo della voce sopra il
     bordo del corpo o sotto il bollino che vi sporge. */
  /** @param {Event} event */
  #allaVoce = (event) => {
    const voce = event.target;
    const corpo = this.#corpo();
    if (!(voce instanceof HTMLDetailsElement) || !voce.open || !corpo) return;

    const area = corpo.getBoundingClientRect();
    const r = voce.getBoundingClientRect();
    const sotto = r.bottom - area.bottom;
    if (sotto <= 0) return;

    const bollino = this.finestra?.querySelector('.finestra__bollino');
    const limite = Math.max(area.top, bollino ? bollino.getBoundingClientRect().bottom : 0);
    const spostamento = Math.min(sotto, r.top - limite);
    if (spostamento <= 0) return;
    corpo.scrollBy({ top: spostamento, behavior: MENO_MOVIMENTO.matches ? 'auto' : 'smooth' });
  };

  /* Desktop: sopra la propria scheda, larga quanto lei (Shop_379_23525).
     Mobile: centrata, a tutta larghezza (CSS). */
  #posiziona() {
    const finestra = this.finestra;
    if (!finestra) return;

    if (!DESKTOP.matches) {
      finestra.classList.remove('finestra--su-scheda');
      return;
    }

    const scheda = this.getBoundingClientRect();
    const alto = Math.min(
      Math.max(scheda.top + 5, MARGINE_FINESTRA),
      Math.max(MARGINE_FINESTRA, window.innerHeight - 300)
    );
    finestra.style.setProperty('--finestra-alto', `${Math.round(alto)}px`);
    finestra.style.setProperty('--finestra-sinistra', `${Math.round(scheda.left)}px`);
    finestra.style.setProperty('--finestra-larghezza', `${Math.round(scheda.width)}px`);
    finestra.classList.add('finestra--su-scheda');
  }

  /* Clic sul fondo velato (fuori dalla finestra): chiude. */
  /** @param {MouseEvent} event */
  #alClicFinestra = (event) => {
    const finestra = this.finestra;
    if (!finestra || event.target !== finestra) return;

    const r = finestra.getBoundingClientRect();
    const dentro =
      event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!dentro) finestra.close();
  };

  #allaChiusura = () => {
    window.removeEventListener('resize', this.#alRidimensionamento);
    this.finestra?.style.removeProperty('height');
    this.apertoDa?.focus();
    this.apertoDa = null;
  };

  /** @param {string} testo */
  #scriviEsito(testo) {
    const esito = this.querySelector('[data-scheda-esito]');
    if (esito) esito.textContent = testo;
  }

  /** @param {SubmitEvent} event */
  #alInvio = async (event) => {
    const form = this.form;
    if (!form) return;
    event.preventDefault();

    const aggiungi = this.querySelector('[data-scheda-aggiungi]');
    if (aggiungi instanceof HTMLButtonElement) {
      if (aggiungi.disabled) return;
      aggiungi.disabled = true;
      aggiungi.setAttribute('aria-busy', 'true');
    }
    this.#scriviEsito('');

    const dati = new FormData(form);
    const quantita = Number(dati.get('quantity')) || 1;

    // Le sezioni del carrello (cassetto) si fanno restituire gia' aggiornate
    const sezioni = [];
    for (const elemento of document.querySelectorAll('cart-items-component')) {
      if (elemento instanceof HTMLElement && elemento.dataset.sectionId) sezioni.push(elemento.dataset.sectionId);
    }
    if (sezioni.length) dati.append('sections', sezioni.join(','));

    const attesa = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (dati.get('id')), quantity: quantita }],
        promise: attesa.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: dati });
      const risposta = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const carrello = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
        (r) => r.json()
      );

      if (risposta.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: risposta.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: risposta.description, errors: risposta.errors },
          })
        );
        attesa.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrello),
          detail: { didError: true, items: carrello.items, source: 'scheda-prodotto', itemCount: quantita },
        });
        this.#scriviEsito(risposta.description || risposta.message || '');
        return;
      }

      attesa.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrello),
        detail: {
          didError: false,
          items: carrello.items,
          source: 'scheda-prodotto',
          itemCount: quantita,
          sections: risposta.sections,
        },
      });
      this.#scriviEsito(Theme.translations.added || '');
      setTimeout(() => this.#scriviEsito(''), 5000);
    } catch (errore) {
      console.error(errore);
      attesa.reject(errore);
      this.dispatchEvent(
        new CartErrorEvent({
          error: errore instanceof Error ? errore.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (aggiungi instanceof HTMLButtonElement) {
        aggiungi.removeAttribute('aria-busy');
        const scelta = form.querySelector('input[name="id"]:checked');
        aggiungi.disabled = scelta instanceof HTMLInputElement ? scelta.disabled : false;
      }
    }
  };
}

if (!customElements.get('scheda-prodotto')) {
  customElements.define('scheda-prodotto', SchedaProdotto);
}
